import { useMutation, useQuery } from '@tanstack/react-query';
import {
    forceResetPassword,
    getClientLogo,
    getClientSignInLogo,
    resendForceResetPassword,
    sendEmailVerification,
    validateForceResetPassword,
} from 'api/services';
import { ErrorMessage, Form, Formik } from 'formik';
import { FormikFloatingInput } from 'library/forms';
import ActionButton from 'modules/auth/components/ui/ActionButton';
import { ResetPasswordSchema } from 'modules/auth/pages/reset-password/helpers/reset-password.schemas';
import PasswordEye from 'modules/shared/components/helpers/PasswordEye';
import useAuthSelector from 'modules/shared/hooks/useAuthSelector';
import usePasswordInput from 'modules/shared/hooks/usePasswordInput';
import useTimer from 'modules/shared/hooks/useTimer';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import OtpInput from 'react18-input-otp';
import { authActions } from 'store/auth';
import { twMerge } from 'tailwind-merge';

export enum ResetPasswordStepEnum {
    SEND_EMAIL = 'SEND_EMAIL',
    VERIFY_OTP = 'VERIFY_OTP',
    SEND_PASSWORD = 'SEND_PASSWORD',
}

interface IResetPasswordForm {
    password: string;
    confirmPassword: string;
}

const ForceResetPasswordPage = () => {
    const { user } = useAuthSelector();

    const dispatch = useDispatch();

    const [currentStep, setCurrentStep] = useState<ResetPasswordStepEnum>(ResetPasswordStepEnum.SEND_EMAIL);
    const [otpError, setOtpError] = useState<string>(null);
    const [otp, setOtp] = useState<string>('');

    const timer = useTimer({ initialCount: 60 });
    const clientLogoData = useQuery({
        queryKey: ['appClientLogo'],
        queryFn: () => getClientLogo(window.location.host),
    });

    const clientSignInLogoData = useQuery({
        queryKey: ['appClientSignLogo'],
        queryFn: () => getClientSignInLogo(window.location.host),
    });

    const logoUrl = clientSignInLogoData.data?.signInLogoUrl || clientLogoData.data?.logoUrl;

    const [passwordInputType, togglePasswordInputType] = usePasswordInput();
    const [confirmPasswordInputType, toggleConfirmPasswordInputType] = usePasswordInput();

    const sendEmailVerificationMutation = useMutation(() => sendEmailVerification());
    const resendResetPasswordMutation = useMutation(() => resendForceResetPassword());
    const resetPasswordMutation = useMutation((password: string) => forceResetPassword(password));
    const validateOtpForceResetMutation = useMutation((otp: string) => validateForceResetPassword({ otp }));

    useEffect(() => {
        timer.reset();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);

    const submitEmailVerification = async () => {
        try {
            await sendEmailVerificationMutation.mutateAsync();
            setCurrentStep(ResetPasswordStepEnum.VERIFY_OTP);
        } catch (err: any) {
            toast.error(err.response.data.message || 'Some error occurred. Please try again');
        }
    };

    const handleResendOtp = async () => {
        try {
            await resendResetPasswordMutation.mutateAsync();
            timer.reset();
            toast.success('OTP resent successfully.');
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Some error occurred. Please try again');
        }
    };

    const handleOtpSubmit = async () => {
        if (otp.length < 6) {
            setOtpError('Please enter a valid 6-digit code');
            return;
        }

        try {
            await validateOtpForceResetMutation.mutateAsync(otp);

            setCurrentStep(ResetPasswordStepEnum.SEND_PASSWORD);
            setOtp('');
        } catch (err: any) {
            setOtpError(err.response?.data?.message || 'Invalid OTP. Please try again');
        }
    };

    const handleSubmitPassword = async (values: IResetPasswordForm) => {
        try {
            await resetPasswordMutation.mutateAsync(values.password);

            dispatch(authActions.logout());
        } catch (err: any) {
            toast.error(err.response.data.message || 'Some error occurred. Please try again');
        }
    };

    return (
        <div>
            <div className="bg-white p-8 sm:p-[52px] rounded-lg">
                <div className="flex items-center justify-between gap-4">
                    {logoUrl && (
                        <div>
                            <img src={logoUrl} alt="logo" className="h-16 object-contain" />
                        </div>
                    )}
                </div>

                <div className="mt-10">
                    <h2>Suspicious Activity Detected on Your Account</h2>

                    <div className="text-grayscale-500 mt-3">
                        We have disabled access to your account due to suspicious activity. You need to verify your
                        identity and reset your password to regain access to your account.
                    </div>

                    <div className="mt-8">
                        <div className="bg-card-color rounded-b-2xl max-w-2xl">
                            <div
                                className={twMerge(
                                    currentStep === ResetPasswordStepEnum.SEND_EMAIL ? 'block' : 'hidden'
                                )}
                            >
                                <div className="flex flex-col gap-6 mt-8">
                                    <ActionButton
                                        onClick={submitEmailVerification}
                                        loadingText="Sending Email Verification"
                                        isLoading={sendEmailVerificationMutation.isPending}
                                    >
                                        Send Email Verification
                                    </ActionButton>
                                </div>
                            </div>

                            <div
                                className={twMerge(
                                    currentStep === ResetPasswordStepEnum.VERIFY_OTP ? 'block' : 'hidden'
                                )}
                            >
                                <div className="flex flex-col">
                                    <div className="text-grayscale-500 max-w-xl">
                                        We've sent you a confirmation code on your email,{' '}
                                        <span className="font-medium">{user?.email}</span>
                                    </div>
                                    <div>
                                        <OtpInput
                                            inputProps={{
                                                style: undefined,
                                            }}
                                            containerStyle="flex items-center gap-3 sm:gap-5 mt-4"
                                            inputStyle="text-grayscale-900 text-xl font-semibold text-center w-full sm:w-14 h-12 sm:h-14 border border-grayscale-300 rounded-lg focus:border-primary focus:outline focus:outline-2 focus:outline-primary/10"
                                            errorStyle="border-[#D55650] outline outline-[#D55650]/10 focus:border-[#D55650] focus:outline-[#D55650]/10"
                                            value={otp}
                                            onChange={(val: string) => {
                                                setOtp(val);
                                                setOtpError(null);
                                            }}
                                            numInputs={6}
                                            hasErrored={Boolean(otpError)}
                                        />

                                        {otpError && <div className="form-error-message text-sm mt-2">{otpError}</div>}

                                        <div className="mt-4">
                                            <div className="text-grayscale-500">
                                                Not received your code ?{' '}
                                                {timer.disabled ? (
                                                    <>
                                                        <span className="text-primary ">{timer.seconds}s</span>
                                                    </>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="text-primary font-medium cursor-pointer"
                                                        onClick={handleResendOtp}
                                                    >
                                                        Resend Code
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-6 mt-8">
                                            <ActionButton
                                                isLoading={validateOtpForceResetMutation.isPending}
                                                loadingText="Verifying email"
                                                disabled={otp.length < 6}
                                                onClick={handleOtpSubmit}
                                            >
                                                Verify Email
                                            </ActionButton>
                                        </div>
                                    </div>
                                    <button
                                        className="text-sm text-center text-grayscale-500 mt-3 font-medium"
                                        onClick={() => setCurrentStep(ResetPasswordStepEnum.SEND_EMAIL)}
                                    >
                                        Back
                                    </button>
                                </div>
                            </div>

                            <div
                                className={twMerge(
                                    currentStep === ResetPasswordStepEnum.SEND_PASSWORD ? 'block' : 'hidden'
                                )}
                            >
                                <div className="flex flex-col">
                                    <Formik
                                        initialValues={{ password: '', confirmPassword: '' }}
                                        enableReinitialize
                                        validationSchema={ResetPasswordSchema}
                                        onSubmit={handleSubmitPassword}
                                    >
                                        <Form>
                                            <div>
                                                <div className="relative">
                                                    <FormikFloatingInput
                                                        type={passwordInputType}
                                                        name="password"
                                                        label="New Password"
                                                        className="pr-12"
                                                    />

                                                    <button
                                                        type="button"
                                                        className="absolute  right-[14px] top-[50%] translate-y-[-50%] text-grayscale-400"
                                                        onClick={togglePasswordInputType}
                                                    >
                                                        <PasswordEye type={passwordInputType} />
                                                    </button>
                                                </div>

                                                <ErrorMessage
                                                    component="div"
                                                    className="form-error-message text-sm"
                                                    name="password"
                                                />
                                            </div>

                                            <div className="mt-5">
                                                <div className="relative">
                                                    <FormikFloatingInput
                                                        type={confirmPasswordInputType}
                                                        name="confirmPassword"
                                                        label="Confirm new password"
                                                        className="pr-12"
                                                    />

                                                    <button
                                                        type="button"
                                                        className="absolute  right-[14px] top-[50%] translate-y-[-50%] text-grayscale-400"
                                                        onClick={toggleConfirmPasswordInputType}
                                                    >
                                                        <PasswordEye type={confirmPasswordInputType} />
                                                    </button>
                                                </div>

                                                <ErrorMessage
                                                    component="div"
                                                    className="form-error-message text-sm"
                                                    name="confirmPassword"
                                                />
                                            </div>

                                            <div className="flex flex-col gap-6 mt-8">
                                                <ActionButton
                                                    loadingText="Confirming"
                                                    isLoading={resetPasswordMutation.isPending}
                                                >
                                                    Confirm
                                                </ActionButton>
                                            </div>
                                        </Form>
                                    </Formik>
                                    <button
                                        className="text-sm text-center text-grayscale-500 font-medium mt-3"
                                        onClick={() => setCurrentStep(ResetPasswordStepEnum.SEND_EMAIL)}
                                    >
                                        Back
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-14">
                        <div className="flex items-center gap-3">
                            <div className="text-grayscale-500">Avaliable on</div>
                            <div className="flex items-center gap-4">
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://apps.apple.com/in/app/progresscenter/id1561585562"
                                    className="grid place-items-center w-9 h-9 rounded-full border border-grayscale-300"
                                >
                                    <img src="/icons/ios.svg" alt="" />
                                </a>

                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://play.google.com/store/apps/details?id=com.progresscenter.app"
                                    className="grid place-items-center w-9 h-9 rounded-full border border-grayscale-300"
                                >
                                    <img src="/icons/android.svg" alt="" />
                                </a>

                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://d14os2kprk7f9m.cloudfront.net/apps/v4/ProgressCenter.dmg"
                                    className="grid place-items-center w-9 h-9 rounded-full border border-grayscale-300"
                                >
                                    <img src="/icons/apple.svg" alt="" />
                                </a>

                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://d14os2kprk7f9m.cloudfront.net/apps/v4/ProgressCenter.zip"
                                    className="grid place-items-center w-9 h-9 rounded-full border border-grayscale-300"
                                >
                                    <img src="/icons/windows.svg" alt="" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForceResetPasswordPage;
